import gql from 'graphql-tag';
import GraphQLClient from '../../adapter/GraphQLClient';
import { UserId } from '@kaizenplatform/kaizen-id-entity';
import Work, { WorkId } from '../../entity/Work';

class WorkWebApi {
  private graphqlClient: GraphQLClient;

  constructor({ offerGraphqlClient }: { offerGraphqlClient: GraphQLClient }) {
    this.graphqlClient = offerGraphqlClient;
  }

  public getWorksById = async ({
    userId,
    itemCount,
    startCursor,
  }: {
    userId: UserId;
    itemCount: number;
    startCursor: string | null;
  }): Promise<{ items: Work[]; endCursor: string | null }> => {
    try {
      const result = await this.graphqlClient.query(
        gql`
          query WorksByUser($userId: ID!, $first: Int, $after: String) {
            WorksByUser(userId: $userId, first: $first, after: $after) {
              edges {
                node {
                  id
                  user {
                    id
                    name
                    profileImageUrl
                  }
                  organization {
                    id
                    name
                    profileImageUrl
                  }
                  creativeType {
                    key
                    name
                  }
                  assetUrl
                  thumbnailUrl
                  intent
                  offerName
                  createdAt
                  updatedAt
                  achievementName
                }
                cursor
              }
              pageInfo {
                endCursor
                hasNextPage
              }
            }
          }
        `,
        {
          userId,
          first: itemCount,
          after: startCursor,
        },
      );

      const { edges, pageInfo } = result.WorksByUser;

      if (!Array.isArray(edges)) {
        throw new Error('result.WorksByUser.edges must be an array.');
      }

      return {
        items: result.WorksByUser.edges.map((edge: any) => Work.fromJSON(edge.node)),
        endCursor: pageInfo.hasNextPage && pageInfo.endCursor ? pageInfo.endCursor : null,
      };
    } catch ({ type }) {
      if (type === 'UnauthorizedError') {
        throw new UnauthorizedError({
          userId,
        });
      }
      throw new WorksNotFound({
        userId,
      });
    }
  };

  public getWorkById = async (workId: WorkId): Promise<Work> => {
    const result = await this.graphqlClient.query(
      gql`
        query Work($workId: ID!) {
          Work(workId: $workId) {
            id
            user {
              id
              name
              profileImageUrl
            }
            organization {
              id
              name
              profileImageUrl
            }
            creativeType {
              key
              name
            }
            assetUrl
            thumbnailUrl
            intent
            offerName
            createdAt
            updatedAt
            achievementName
          }
        }
      `,
      {
        workId,
      },
    );

    if (result.Work === null) {
      throw new WorkNotFound({
        workId,
      });
    }

    return Work.fromJSON(result.Work);
  };
}

export class WorksNotFound extends Error {
  public readonly name = 'worksNotFound';
  public readonly message: string;
  public readonly userId: UserId;

  constructor({ userId }: { userId: UserId }) {
    super();

    this.userId = userId;
    this.message = `The Works by User (id = ${userId}) is not found.`;
  }
}
export class WorkNotFound extends Error {
  public readonly name = 'workNotFound';
  public readonly message: string;
  public readonly workId: UserId;

  constructor({ workId }: { workId: WorkId }) {
    super();

    this.workId = workId;
    this.message = `A Work by WorkId (id = ${workId}) is not found.`;
  }
}

export class UnauthorizedError extends Error {
  public readonly name = 'UnauthorizedError';
  public readonly message: string;
  public readonly userId: UserId;

  constructor({ userId }: { userId: UserId }) {
    super();

    this.userId = userId;
    this.message = `This User (id = ${userId}) is not open to the public.`;
  }
}

export default WorkWebApi;
